import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Spinner, Pagination, Alert, Modal, Tab, Tabs } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getUploadedFiles } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const CsvListPage = () => {
  const [csvFiles, setCsvFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const { hasRole } = useAuth();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedErrorSummary, setSelectedErrorSummary] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  const PAGE_SIZE = 10;

  useEffect(() => {
    const fetchCsvFiles = async () => {
      setLoading(true);
      try {
        const data = await getUploadedFiles({
          skip: (page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
          upload_type: activeTab === "all" ? null : activeTab,
        });
        setCsvFiles(data.uploads);
        setTotalRecords(data.total);
        setTotalPages(Math.ceil(data.total / PAGE_SIZE));
      } catch (err) {
        console.error('Error fetching CSV files:', err);
        setError('Failed to load uploaded CSV files. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCsvFiles();
  }, [page, activeTab]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const renderStatus = (csv) => {
    if (csv.completed) {
      return <Badge bg="success">Completed</Badge>;
    } else {
      return (
        <div>
          <Badge bg="warning">Processing</Badge>
          <span className="ms-2">{csv.percentage}%</span>
        </div>
      );
    }
  };

  if (loading && csvFiles.length === 0) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  const formatErrorSummary = (errorSummary) => {
    if (!errorSummary || !errorSummary.sample_errors)
      return "No error details available";

    const errors = [];
    Object.entries(errorSummary.sample_errors).forEach(
      ([errorType, errorMessage]) => {
        // Clean up database error messages
        if (errorType === "database_errors") {
          if (errorMessage.includes("Data too long")) {
            // Extract column name from error message
            const columnMatch = errorMessage.match(/column '(\w+)'/);
            const columnName = columnMatch ? columnMatch[1] : "unknown field";
            errors.push(`Data too long for '${columnName}' field`);
          } else if (errorMessage.includes("Duplicate entry")) {
            errors.push("Duplicate records found");
          } else {
            errors.push("Database validation error");
          }
        }
      }
    );

    return errors.join(", ");
  };

  const renderUploadType = (uploadType) => {
    if (uploadType === "cibil") {
      return <Badge bg="info">CIBIL Data</Badge>;
    } else {
      return <Badge bg="primary">Regular Upload</Badge>;
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Uploaded CSV Files</h2>
        {(hasRole("admin") || hasRole("maker")) && (
          <Link to="/upload">
            <Button variant="primary">Upload New CSV</Button>
          </Link>
        )}
        {(hasRole("admin") || hasRole("cibil_uploader")) && (
          <Link to="/cibil-upload">
            <Button variant="primary">Upload New Cibil CSV</Button>
          </Link>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Header>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k);
              setPage(1); // Reset to first page when switching tabs
            }}
            className="mb-0"
          >
            <Tab eventKey="all" title={`All Uploads (${totalRecords})`} />
            <Tab eventKey="regular" title="Regular Uploads" />
            <Tab eventKey="cibil" title="CIBIL Data" />
          </Tabs>
        </Card.Header>
        <Card.Body>
          {csvFiles.length === 0 ? (
            <div className="text-center p-5">
              <p>No CSV files found for the selected type.</p>
              {activeTab !== "all" && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setActiveTab("all")}
                >
                  View All Uploads
                </Button>
              )}
              {(hasRole("maker") || hasRole("admin")) &&
                activeTab !== "cibil" && (
                  <Link to="/upload">
                    <Button variant="primary" className="ms-2">
                      Upload Regular CSV
                    </Button>
                  </Link>
                )}
              {(hasRole("cibil_uploader") || hasRole("admin")) &&
                activeTab !== "regular" && (
                  <Link to="/cibil-upload">
                    <Button variant="primary" className="ms-2">
                      Upload CIBIL CSV
                    </Button>
                  </Link>
                )}
            </div>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>File Name</th>
                    <th>Type</th>
                    <th>Uploaded</th>
                    <th>Total Rows</th>
                    <th>Valid Rows</th>
                    <th>Processed</th>
                    {(activeTab === 'all' || activeTab === 'cibil') && <th>Results</th>}
                    {/* <th>Status</th> */}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {csvFiles.map((csv) => (
                    <tr key={csv.csv_id}>
                      <td>{csv.file_name}</td>
                      <td>{renderUploadType(csv.upload_type)}</td>
                      <td>{formatDate(csv.uploaded_at)}</td>
                      <td>{csv.total_rows}</td>
                      <td>{csv.valid_rows}</td>
                      <td>
                        {csv.processed} / {csv.valid_rows}
                      </td>
                      {(activeTab == 'all' || activeTab == 'cibil') && 
                        <td>
                          {csv.leads_created !== undefined ? (
                            <div>
                              <small className="text-success d-block">
                                Created: {csv.leads_created || 0}
                              </small>
                              <small className="text-info d-block">
                                Updated: {csv.leads_updated || 0}
                              </small>
                              {csv.leads_failed > 0 && (
                                <div>
                                  <small className="text-danger d-block">
                                    Failed: {csv.leads_failed}
                                  </small>
                                  {csv.error_summary && (
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 text-danger"
                                      onClick={() => {
                                        setSelectedErrorSummary(
                                          csv.error_summary
                                        );
                                        setSelectedFileName(csv.file_name);
                                        setShowErrorModal(true);
                                      }}
                                    >
                                      View Errors
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                      }
                      {/* <td>{renderStatus(csv)}</td> */}
                      <td>
                        {!hasRole("maker") && (
                          <Link to={`/csvs/${csv.csv_id}/leads`}>
                            <Button variant="outline-primary" size="sm">
                              View Leads
                            </Button>
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                      disabled={page === 1}
                    />

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      // Create a window of pages around the current page
                      let pageNum;
                      if (totalPages <= 5) {
                        // If we have 5 or fewer pages, show them all
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        // If we're near the start, show pages 1-5
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        // If we're near the end, show the last 5 pages
                        pageNum = totalPages - 4 + i;
                      } else {
                        // Otherwise show 2 pages before and 2 pages after current
                        pageNum = page - 2 + i;
                      }

                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === page}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}

                    <Pagination.Next
                      onClick={() =>
                        setPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={page === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setPage(totalPages)}
                      disabled={page === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
      {/* Error Details Modal */}
      <Modal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Error Details - {selectedFileName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedErrorSummary && (
            <div>
              <h6>Error Summary:</h6>
              {Object.entries(selectedErrorSummary.error_counts || {}).map(
                ([errorType, count]) => (
                  <div key={errorType} className="mb-2">
                    <Badge bg="danger" className="me-2">
                      {errorType.replace("_", " ")}
                    </Badge>
                    <span>{count} records failed</span>
                  </div>
                )
              )}

              <hr />

              <h6>Sample Errors:</h6>
              {Object.entries(selectedErrorSummary.sample_errors || {}).map(
                ([errorType, errorMessage]) => (
                  <div key={errorType} className="mb-3">
                    <strong>{errorType.replace("_", " ")}:</strong>
                    <div className="bg-light p-2 mt-1 rounded">
                      <small className="text-muted">
                        {formatErrorSummary({
                          sample_errors: { [errorType]: errorMessage },
                        })}
                      </small>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CsvListPage;